import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAIService } from '@/context/AIServiceContext';
import InlineHexagonLoader from '@/components/HexagonLoader/InlineHexagonLoader';
import InsightMarkdown from './InsightMarkdown';
import * as Sentry from '@sentry/react';

// Increased retry delay to 120 seconds for better rate limit handling
const RETRY_DELAY_MS = 120000; // 120s (increased from 60s)
const MAX_RETRIES = 3;
// Stagger delay for this component (loads after risk register)
const STAGGER_DELAY_MS = 6000; // 6s delay to avoid rate limiting

/**
 * RiskAnalysis - AI-generated analysis of IT risks with remediation recommendations
 * Provides context about severity and real-world implications
 * 
 * Features:
 * - Staggered loading (6s delay) to avoid rate limits
 * - Non-blocking (page loads while AI content generates)
 * - Increased retry delays (120s instead of 60s)
 * - Graceful error handling
 */
export default function RiskAnalysis() {
    const { apiFetch } = useAuth();
    const { aiEnabled, isLoading: aiLoading } = useAIService();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [analysis, setAnalysis] = useState('');
    const [reloadToken, setReloadToken] = useState(0);
    const retryTimeoutRef = useRef(null);
    const attemptRef = useRef(0);
    const staggerTimeoutRef = useRef(null);
    const [staggerComplete, setStaggerComplete] = useState(false);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            if (staggerTimeoutRef.current) clearTimeout(staggerTimeoutRef.current);
        };
    }, []);

    // Stagger loading to avoid rate limits
    useEffect(() => {
        if (aiLoading) return;
        if (!aiEnabled) return;

        // Wait for stagger delay before starting to fetch
        staggerTimeoutRef.current = setTimeout(() => {
            setStaggerComplete(true);
        }, STAGGER_DELAY_MS);

        return () => {
            if (staggerTimeoutRef.current) clearTimeout(staggerTimeoutRef.current);
        };
    }, [aiEnabled, aiLoading]);

    // Fetch risk analysis only after stagger completes
    useEffect(() => {
        if (aiLoading) return;
        if (!aiEnabled) return;
        if (!staggerComplete) return;

        let active = true;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const res = await apiFetch('/api/insights/it-risk-analysis');
                if (res.status === 204) {
                    if (active) {
                        setAnalysis('');
                        attemptRef.current = 0;
                    }
                    return;
                }
                if (res.status === 429) throw new Error('RATE_LIMIT');
                if (res.status === 403) throw new Error('FORBIDDEN');
                if (!res.ok) throw new Error('Failed to fetch risk analysis');
                
                const json = await res.json();
                if (active) {
                    setAnalysis(json.analysis || '');
                    attemptRef.current = 0;
                }
            } catch (e) {
                Sentry.captureException(e);
                if (!active) return;
                const msg = e.message || 'Error';
                
                // Rate limit handling with increased retry delay
                if (msg === 'RATE_LIMIT' && attemptRef.current < MAX_RETRIES) {
                    const attempt = attemptRef.current + 1;
                    attemptRef.current = attempt;
                    setError(`Service busy (429). Retrying in 2 minutes (attempt ${attempt}/${MAX_RETRIES})`);
                    retryTimeoutRef.current = setTimeout(() => {
                        setReloadToken(t => t + 1);
                    }, RETRY_DELAY_MS);
                } else if (msg === 'FORBIDDEN') {
                    setError('AI Analytics is disabled or API key is invalid.');
                } else {
                    setError(msg === 'RATE_LIMIT' ? 'Service busy. Please try again later.' : msg);
                }
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; };
    }, [aiEnabled, aiLoading, staggerComplete, apiFetch, reloadToken]);

    // Show placeholder if AI is loading config
    if (aiLoading) {
        return (
            <div className="ai-commentary-box" style={{ 
                marginTop: 16, 
                padding: 16, 
                background: '#fff8e1', 
                borderRadius: 8,
                borderLeft: '4px solid #ff9800'
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    marginBottom: 12,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#e65100'
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>AI Risk Analysis & Remediation Recommendations</span>
                </div>
                <div style={{ textAlign: 'center', color: '#666', fontSize: 13 }}>
                    <InlineHexagonLoader />
                    <div style={{ marginTop: 8 }}>Waiting for AI Insights...</div>
                </div>
            </div>
        );
    }
    
    // Don't render if AI is disabled
    if (!aiEnabled) return null;

    // Show loading state
    if (loading && !analysis && !error) {
        return (
            <div className="ai-commentary-box" style={{ 
                marginTop: 16, 
                padding: 16, 
                background: '#fff8e1', 
                borderRadius: 8,
                borderLeft: '4px solid #ff9800'
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    marginBottom: 12,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#e65100'
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>AI Risk Analysis & Remediation Recommendations</span>
                </div>
                <div style={{ textAlign: 'center', color: '#666', fontSize: 13 }}>
                    <InlineHexagonLoader />
                    <div style={{ marginTop: 8 }}>Generating AI insights...</div>
                </div>
            </div>
        );
    }

    // Show error (with appropriate color for busy vs error)
    if (!analysis && error) {
        return (
            <div className="ai-commentary-box" style={{ 
                marginTop: 16, 
                padding: 16, 
                background: '#fff3e0', 
                borderRadius: 8,
                borderLeft: '4px solid #ff9800',
                color: error.startsWith('Service busy') ? '#b36b00' : '#d32f2f'
            }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                    Risk Analysis
                </div>
                <div style={{ fontSize: 13 }}>
                    {error}
                </div>
            </div>
        );
    }

    // Don't render if no content
    if (!analysis) return null;

    return (
        <div className="ai-commentary-box" style={{ 
            marginTop: 16, 
            padding: 16, 
            background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)', 
            borderRadius: 8,
            borderLeft: '4px solid #ff9800'
        }}>
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8, 
                marginBottom: 12,
                fontSize: 14,
                fontWeight: 600,
                color: '#e65100'
            }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>AI Risk Analysis & Remediation Recommendations</span>
            </div>
            <div style={{ 
                fontSize: 14, 
                lineHeight: 1.6, 
                color: '#333'
            }}>
                <InsightMarkdown text={analysis} />
            </div>
        </div>
    );
}
